/* eslint-disable import/order */
/**
 * Routes configuration and ACL.
 *
 * This is the place you define all routes and their corresponding ACL (based
 * on user roles).
 *
 * The route configs are exported and imported by index.ts where it is used for
 * rigging up the router. We have just extracted it to here for readability.
 *
 * Restrict access to routes based on user role:
 * ---------------------------------------------
 *
 * In the optional 'meta' object of a route, you may specify a 'restrictTo' property
 * that contains an array of AUTHUSERROLE's allowed to visit that route, e.g.:
 * `meta: { restrictTo: [AUTHUSERROLE.DEV, AUTHUSERROLE.ADMIN] }`
 *
 * The router logic in index.ts takes care of implementing the access restriction
 * by comparing the logged-in users roles with the allowed roles you specify.
 *
 * Notes:
 *
 * - If a route has NO `restrictTo` property it will be publicly accessible,
 *  unless it is nested within a route that has restrictions.
 *
 * - You don't have set a 'restrictTo' property on a child route if it's parent
 *  route already has the desired restrictions. All 'restrictTo' in the hierarchy
 *  down to a child route are evaluated. All, including parents' restrictTo, must
 *  pass for access to be granted.
 *
 * - Careful: if the 'restrictTo' property is an empty array [], NO ONE can access
 *  the route. If you want no restrictions on a route just remove the 'restrictTo'
 *  property form the route.
 *
 * - Note: the router (index.ts) contains additional router logic not expressed
 *  here, e.g. redirects based on authentication status.
 *
 */
// eslint-disable import/order
import { MyRouteConfig } from './MyRouteConfig'
import { AUTHUSERROLE } from '../auth/authRoles'

// General pages
import LoginPage from '../views/login/LoginPage.vue'
import LoginForm from '../views/login/loginforms/LoginForm.vue'
import LoggedOut from '../views/login/loginforms/LoggedOut.vue'
import About from '../views/About.vue'

// Client pages
import ClientsPage from '@/views/therapist/clients/ClientsPage.vue'
import ClientPage from '@/views/therapist/client/ClientPage.vue'
import ClientInfo from '@/views/therapist/client/subpages/clientinfo/ClientInfo.vue'
import Journal from '@/views/therapist/client/subpages/journal/Journal.vue'

// Study pages
import StudyPage from '../views/admin/study/study/StudyPage.vue'
import StudyParticipants from '@/views/admin/study/study/subpages/participants/Participants.vue'
import StudyParticipantPage from '@/views/admin/study/case/ParticipantPage.vue'

// System pages
import ApiTest from '@/views/dev/ApiTest.vue'
import SysInfo from '@/views/dev/SysInfo.vue'
import LayoutHelper from '@/views/dev/LayoutHelper.vue'
import AppLayout from '../views/AppLayout.vue'

export const routes: Array<MyRouteConfig> = [
  {
    path: '/login',
    name: '',
    component: LoginPage,
    children: [
      {
        path: '/',
        name: 'login',
        component: LoginForm,
      },
      {
        path: '/goodbye',
        name: 'goodbye',
        component: LoggedOut,
      },
    ],
  },
  {
    // Note that the landing page is also dynamically determined
    // depending on user role - see code in index.ts.
    path: '/',
    component: AppLayout,
    name: 'home',
    meta: {
      restrictTo: [AUTHUSERROLE.DEV, AUTHUSERROLE.ADMIN, AUTHUSERROLE.THERAPIST],
    },
    children: [
      {
        path: '/about',
        name: 'about',
        component: About,
      },
      {
        path: '/clients',
        name: 'clients',
        component: ClientsPage,
      },
      {
        path: '/client/:clientId',
        name: 'client',
        component: ClientPage,
        props: true,
        children: [
          {
            path: 'info',
            name: 'clientInfo',
            props: true,
            component: ClientInfo,
          },
          {
            path: 'journal',
            name: 'journal',
            props: true,
            component: Journal,
          },
        ],
      },
    ],
  },
  {
    path: '/dev',
    component: AppLayout,
    name: 'dev',
    meta: {
      restrictTo: [AUTHUSERROLE.DEV, AUTHUSERROLE.ADMIN, AUTHUSERROLE.THERAPIST],
    },
    children: [
      {
        path: 'layout',
        name: 'layout',
        component: LayoutHelper,
      },
      {
        path: 'apitest',
        name: 'apitest',
        component: ApiTest,
      },
      {
        path: 'sysinfo',
        name: 'sysinfo',
        component: SysInfo,
      },
    ],
  },
  {
    path: '/admin',
    component: AppLayout,
    name: 'admin',
    meta: {
      restrictTo: [AUTHUSERROLE.DEV, AUTHUSERROLE.ADMIN],
    },
    children: [
      {
        path: 'studies/:studyId',
        name: 'study',
        component: StudyPage,
        props: true,
        children: [
          // default sub route:
          {
            path: '/',
            redirect: { name: 'studyCases' },
          },
          {
            path: 'cases',
            name: 'studyCases',
            props: true,
            component: StudyParticipants,
          },
          {
            path: 'journal',
            name: 'journal2',
            props: true,
            component: Journal,
          },
        ],
      },
      {
        path: 'studies/:studyId/cases/:caseId',
        name: 'studyCase',
        component: StudyParticipantPage,
        props: true,
      },

    ],
  },
  {
    path: '/about',
    name: 'About',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import(/* webpackChunkName: "about" */ '../views/About.vue'),
  },

]
